import { Badge } from "../../../../../components/ui/common/badge";
import { Card, CardContent } from "../../../../../components/ui/common/card";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import { IngredientDetailsFormatter } from "../../services/IngredientDetailsFormatter";
import ReadOnlyField from "../shared/ReadOnlyField";

interface IngredientOverviewCardProps {
  ingredientDetails: IngredientRowViewModel | undefined;
}

const CARD_HEADER_CLASS = "tw-text-sm tw-font-semibold tw-text-primary";

const titleCase = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

/**
 * "Ingredient Info" read-only card — Name, Supplier, Product ID, EAN
 * Barcode, Ingredient Type and Data Type (as badges), plus the ingredient
 * image when one is set. Matches the green header-strip card convention used
 * by `ProductDetailsModal`'s Summary tab, and the Name/Supplier fields moved
 * back here now that the drawer header shows type chips instead.
 */
const IngredientOverviewCard = ({ ingredientDetails }: IngredientOverviewCardProps) => {
  const details = ingredientDetails?.supplierIngredientDetails;

  return (
    <Card className="tw-gap-0 tw-overflow-hidden tw-rounded-lg tw-py-0">
      <div className="tw-border-b tw-border-primary/20 tw-px-5 tw-py-3">
        <p className={CARD_HEADER_CLASS}>Ingredient Info</p>
      </div>
      <CardContent className="tw-grid tw-grid-cols-1 tw-gap-5 tw-p-5 sm:tw-grid-cols-2">
        <ReadOnlyField label="Name" value={ingredientDetails?.ingredient_name} />
        <ReadOnlyField label="Supplier" value={ingredientDetails?.supplier_name} />
        <ReadOnlyField label="Product ID" value={details?.product_id} mono />
        <ReadOnlyField label="EAN Barcode" value={details?.ean_bar_code} mono />

        <div>
          <p className="tw-mb-1 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Ingredient Type
          </p>
          <Badge variant="outline" className="tw-rounded-full tw-text-[10px] tw-font-semibold tw-uppercase">
            {titleCase(details?.ingredient_type) || "-"}
          </Badge>
        </div>
        <div>
          <p className="tw-mb-1 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Data Type
          </p>
          <Badge variant="secondary" className="tw-rounded-full tw-text-[10px] tw-font-semibold">
            {IngredientDetailsFormatter.dataTypeLabel(details?.data_type) || "-"}
          </Badge>
        </div>

        {ingredientDetails?.ingredient_image_path ? (
          <div className="sm:tw-col-span-2">
            <p className="tw-mb-1.5 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              Ingredient Image
            </p>
            <img
              src={ingredientDetails.ingredient_image_path}
              alt={ingredientDetails.ingredient_name}
              className="tw-h-24 tw-w-24 tw-rounded-md tw-border tw-border-border tw-object-cover"
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default IngredientOverviewCard;
